import { useCallback } from "react";
import { AchievementType } from "@rosette/ts/src/business/achievement/AchievementType.js";
import type { GameController } from "@rosette/ts/src/business/game/controller/GameController.js";
import { BoardType } from "@rosette/ts/src/royalur/model/shape/BoardType.js";
import type { Game } from "@rosette/ts/src/royalur/Game.js";
import type { PlayerType } from "@rosette/ts/src/royalur/model/PlayerType.js";
import { featureFlags } from "@rosette/ts/src/business/featureFlags.js";
import { Ruleset } from "@rosette/ts/src/business/game/Ruleset.js";
import { MovedGameState } from "@rosette/ts/src/royalur/engine/state/MovedGameState.js";
import { WaitingForMoveGameState } from "@rosette/ts/src/royalur/engine/state/WaitingForMoveGameState.js";
import { Tile } from "@rosette/ts/src/royalur/model/Tile.js";
import { useGrantAchievementMutation } from "@/utils/achievements/useGrantAchievementMutation.js";


/** The tile of the central rosette on the standard board. */
const CENTRAL_ROSETTE_TILE = new Tile(2, 4);


function checkFullDeployAchievement(game: Game, yourPlayer: PlayerType): boolean {
    const currentState = game.getState();
    const board = currentState.getBoard();
    const settings = game.getRules().getSettings();
    const startingPieceCount = settings.getStartingPieceCount();

    const piecesOnBoard = board.countPieces(yourPlayer);
    return piecesOnBoard === startingPieceCount;
}

function checkCrowdedAchievement(game: Game, yourPlayer: PlayerType): boolean {
    const currentState = game.getState();
    const board = currentState.getBoard();
    const settings = game.getRules().getSettings();
    const path = settings.getPaths().get(yourPlayer);

    const boardType = BoardType.getByID(settings.getBoardShape().getID());
    if (boardType !== BoardType.STANDARD && boardType !== BoardType.ASEB)
        return false;

    const column = path[0].getX() - 1;
    for (let row = 0; row < 4; ++row) {
        const piece = board.getByIndices(column, row);
        if (piece === null || piece.getOwner() !== yourPlayer)
            return false;
    }
    return true;
}

function checkStandUrGroundAchievement(game: Game, yourPlayer: PlayerType): boolean {
    // Look for a turn where capturing off the central rosette was your only
    // available capture, and you played a different move to keep holding it.
    const states = game.getStates();
    for (let index = 1; index < states.length; ++index) {
        const movedState = states[index];
        const waitingState = states[index - 1];
        if (!(movedState instanceof MovedGameState)
            || !(waitingState instanceof WaitingForMoveGameState)
            || waitingState.getTurn() !== yourPlayer) {
            continue;
        }

        const captures = waitingState.getAvailableMoves()
            .filter(move => move.isCapture());
        if (captures.length !== 1)
            continue;

        const captureSource = captures[0].getSourceOrNull();
        if (captureSource === null || !captureSource.equals(CENTRAL_ROSETTE_TILE))
            continue;

        const playedSource = movedState.getMove().getSourceOrNull();
        if (playedSource === null || !playedSource.equals(CENTRAL_ROSETTE_TILE))
            return true;
    }
    return false;
}

export function useGameAchievementSystem(controller: GameController) {
    const requestFullDeployAchievement = useGrantAchievementMutation(AchievementType.FULL_DEPLOY);
    const requestCrowdedAchievement = useGrantAchievementMutation(AchievementType.CROWDED);
    const requestStandUrGroundAchievement = useGrantAchievementMutation(AchievementType.STAND_UR_GROUND);
    const gameSource = controller.getGameSource();
    const knownSetupSettings = gameSource.getKnownSetupSettings();
    const knownRuleset = (knownSetupSettings ? knownSetupSettings.getRuleset() : null);

    const registerGameAchievementHooks = useCallback(() => {
        if (!featureFlags.achievementsEnabled)
            return () => {};
        if (!knownRuleset)
            return () => {};

        const isStandardRuleset = (
            knownRuleset === Ruleset.FINKEL
            || knownRuleset === Ruleset.BLITZ
            || knownRuleset === Ruleset.MASTERS
        );
        if (!isStandardRuleset)
            return () => {};

        return gameSource.game.subscribe((game, _event) => {
            const players = controller.getPlayers().get();
            const yourPlayer = players.getYourPlayer();
            if (!yourPlayer)
                return;

            // Full Deploy achievement
            if (!requestFullDeployAchievement.isSuccess
                && !requestFullDeployAchievement.isPending
                && !requestFullDeployAchievement.isError
                && checkFullDeployAchievement(game, yourPlayer)) {
                requestFullDeployAchievement.mutate();
            }

            // Crowded achievement
            if (!requestCrowdedAchievement.isSuccess
                && !requestCrowdedAchievement.isPending
                && !requestCrowdedAchievement.isError
                && checkCrowdedAchievement(game, yourPlayer)) {
                requestCrowdedAchievement.mutate();
            }

            // Stand Ur Ground achievement, only in Finkel
            if (knownRuleset === Ruleset.FINKEL
                && !requestStandUrGroundAchievement.isSuccess
                && !requestStandUrGroundAchievement.isPending
                && !requestStandUrGroundAchievement.isError
                && checkStandUrGroundAchievement(game, yourPlayer)) {
                requestStandUrGroundAchievement.mutate();
            }
        });
    }, [
        controller,
        gameSource,
        knownRuleset,
        requestFullDeployAchievement,
        requestCrowdedAchievement,
        requestStandUrGroundAchievement,
    ]);

    return { registerGameAchievementHooks };
}
